import { useEffect } from "react";
import { useParams, useSearchParams } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BarChart3, Clapperboard, Link2, Puzzle } from "lucide-react";
import { api } from "../lib/api";
import { useCrumbs, useStudio } from "../lib/context";
import { Badge, ErrorBox, Loading } from "../components/ui";
import { ContentEditor } from "../components/ContentEditor";
import { ExerciseEditor } from "../components/ExerciseEditor";
import { CardStatsPanel } from "../components/CardStats";
import { VideoTab } from "../components/VideoTab";

export function NotionPage() {
  const { id = "" } = useParams();
  const [sp, setSp] = useSearchParams();
  const { languages, pushRecent } = useStudio();
  const q = useQuery({ queryKey: ["notion", id], queryFn: () => api.notion(id) });
  const qc = useQueryClient();
  const tab = sp.get("lang") ?? languages[0]?.code ?? "fr";
  const setTab = (t: string) => setSp((p) => { p.set("lang", t); p.delete("review"); return p; }, { replace: true });
  const n = q.data;
  useCrumbs([{ label: "Programme", to: "/programme" }, { label: n?.title ?? "Notion" }]);
  useEffect(() => { if (n) pushRecent({ id: n.id, title: n.title }); }, [n?.id, n?.title, pushRecent]); // eslint-disable-line react-hooks/exhaustive-deps

  if (q.isLoading) return <Loading lines={10} />;
  if (q.error || !n) return <ErrorBox error={q.error} onRetry={() => q.refetch()} />;
  const statusOf = (lang: string) => {
    const xs = n.contents.filter((c) => c.lang === lang);
    return xs.some((c) => c.status === "VALIDATED") ? "VALIDATED" : xs.some((c) => c.status === "IN_REVIEW") ? "IN_REVIEW" : xs.length ? "DRAFT" : "MISSING";
  };

  return (
    <>
      <div className="notion-head">
        <div style={{ flex: 1, minWidth: 260 }}>
          <div className="kicker"><span className="mono" style={{ textTransform: "none", letterSpacing: 0 }}>{n.slug}</span></div>
          <h1>{n.title}</h1>
        </div>
        {n.prerequisites.length > 0 && (
          <div className="row wrap" style={{ gap: 6, alignSelf: "flex-end" }}>
            <span className="small muted strong row" style={{ gap: 5 }}><Link2 size={14} />Prérequis</span>
            {n.prerequisites.map((p) => <span key={p.title} className="chip static">{p.title}</span>)}
          </div>
        )}
      </div>
      <div className="tabs" role="tablist" aria-label="Contenus de la notion">
        {languages.map((l) => (
          <button key={l.code} role="tab" aria-selected={tab === l.code} className={`tab ${tab === l.code ? "active" : ""}`} onClick={() => setTab(l.code)}>
            {l.nativeName ?? l.name} <Badge status={statusOf(l.code)} />
          </button>
        ))}
        <button role="tab" aria-selected={tab === "exercises"} className={`tab ${tab === "exercises" ? "active" : ""}`} onClick={() => setTab("exercises")}>
          <Puzzle size={15} />Exercices <span className="muted">({n.exercises.length})</span>
        </button>
        <button role="tab" aria-selected={tab === "video"} className={`tab ${tab === "video" ? "active" : ""}`} onClick={() => setTab("video")}><Clapperboard size={15} />Vidéo</button>
        <button role="tab" aria-selected={tab === "stats"} className={`tab ${tab === "stats" ? "active" : ""}`} onClick={() => setTab("stats")}><BarChart3 size={15} />Statistiques</button>
      </div>
      {tab === "video" ? <VideoTab languages={languages.map((l) => ({ code: l.code, label: l.nativeName ?? l.name }))}
        source={{ kind: "contents", title: n.title, versions: n.contents, save: (lang, body) => api.saveContent(n.id, lang, body, false), refresh: async () => { await qc.refetchQueries({ queryKey: ["notion", n.id] }); } }} /> : tab === "stats" ? <CardStatsPanel notionId={n.id} /> : tab === "exercises"
        ? <ExerciseEditor notion={n} />
        : <ContentEditor key={`${n.id}-${tab}`} notion={n} lang={tab} reviewId={sp.get("review")}
          insertModel={sp.get("insertModel")} onInserted={() => setSp((p) => { p.delete("insertModel"); return p; }, { replace: true })} />}
    </>
  );
}
